import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ownershipTransfersApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  MarkOwnershipTransferDeletedRequest,
  MarkOwnershipTransferKeptRequest,
  MarkOwnershipTransferReassignedRequest,
  OwnershipTransferListFilter,
} from '../types/api.js';

/**
 * ownership_transfers 一覧取得フック。等価フィルタ (originalOwnerEmail / reviewStatus /
 * resourceType) を渡すと API 側で `where(==)` に積まれる。`transferredAt` 降順は server 側で
 * 行う (上限 500 件)。
 *
 * @graph-stack cortex-admin
 * @graph-domain HR
 * @graph-business ownership_transfers 一覧取得フック。TanStack Query でキャッシュ管理
 * @graph-connects ownershipTransfersApi [calls] API クライアント経由で取得
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useOwnershipTransfers(filter: OwnershipTransferListFilter) {
  return useQuery({
    queryKey: queryKeys.ownershipTransfers.list({
      originalOwnerEmail: filter.originalOwnerEmail,
      reviewStatus: filter.reviewStatus,
      resourceType: filter.resourceType,
    }),
    queryFn: () => ownershipTransfersApi.list(filter),
    staleTime: 30_000,
  });
}

/**
 * 共通 mutation factory。各 mark* 成功時に list / detail 両方のキャッシュを invalidate する。
 * @graph-connects ownershipTransfersApi [calls] API クライアント経由で確定
 * @graph-connects queryKeys [calls] キャッシュ invalidate キー
 */
function useMarkMutation<Input>(
  operation: (input: Input) => Promise<unknown>,
  successKey: 'markKept' | 'markReassigned' | 'markDeleted',
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: operation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ownershipTransfers.all });
    },
    meta: { successKey },
  });
}

/** kept 確定 mutation。 @graph-connects useMarkMutation [calls] 共通 mutation factory */
export function useMarkOwnershipKept() {
  return useMarkMutation<MarkOwnershipTransferKeptRequest>(
    ownershipTransfersApi.markKept,
    'markKept',
  );
}

/** reassigned 確定 mutation。 @graph-connects useMarkMutation [calls] 共通 mutation factory */
export function useMarkOwnershipReassigned() {
  return useMarkMutation<MarkOwnershipTransferReassignedRequest>(
    ownershipTransfersApi.markReassigned,
    'markReassigned',
  );
}

/** deleted 確定 mutation。 @graph-connects useMarkMutation [calls] 共通 mutation factory */
export function useMarkOwnershipDeleted() {
  return useMarkMutation<MarkOwnershipTransferDeletedRequest>(
    ownershipTransfersApi.markDeleted,
    'markDeleted',
  );
}
